import { AlertCircle } from "lucide-react";

/**
 * Shared empty-error state for dashboard tabs. Renders a consistent
 * "we couldn't load this" panel so each tab doesn't reinvent its own.
 *
 * Intentionally minimal: no retry button. The follow-up task adds
 * retry + per-section refetch wiring on top of TanStack Query's
 * `useQuery({ ... }).refetch`. Until then this surface at least
 * prevents the previous behavior of silently rendering an empty page
 * when a fetch fails.
 */
export function DashboardErrorState({
  title = "We couldn't load this section",
  description = "Something went wrong while loading your dashboard. Please refresh the page to try again.",
}: {
  title?: string;
  description?: string;
}) {
  return (
    <div
      data-testid="dashboard-error-state"
      role="alert"
      className="flex min-h-[40vh] items-center justify-center px-4"
    >
      <div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
          <AlertCircle className="h-5 w-5 text-red-700" aria-hidden="true" />
        </div>
        <h2 className="text-base font-semibold text-red-900">{title}</h2>
        <p className="mt-2 text-sm text-red-800">{description}</p>
      </div>
    </div>
  );
}
